import React, { useState, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
// Import Shared Components
import { Checkbox } from 'shared-components';
import { ThemeContext } from 'styled-components';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Shared Utils
import { post } from 'shared-components/src/utils/http';
// Import Local Layouts
import AuthLayout from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
// Import Local Components
import ActivationMessage from '../components/ActivationMessage';
// Improt Component Styles
import { FormRow } from '../styles';

import { cardFieldError } from '../../../utils/validationHandlers';

import { authEn } from '../../../i18n/auth';
import CardholderAgreementLink  from './components/CardholderAgreementLink';
import PrivacyPolicyAgreementLink  from './components/PrivacyPolicyAgreementLink';
import ElectronicComConsentLink  from './components/ElectronicComConsentLink';
import CardFields from './components/CardFields';
import { digitsOnly, expandExpiry } from '../../../utils/cardFormatting';
import { activationConsentsAccepted } from '../../../utils/activationConsents';

export default () => {
  const intl = useIntl();
  const page = 'activate';
  const theme = useContext(ThemeContext);

  // // Set States
  const initialCardInfoState = {
    cardNumber: '',
    cvv: '',
    expiry: '',
    consent: false,
  };
  const initialValidationErrors = {
    cardNumber: '',
    cvv: '',
    expiry: '',
    consent: false,
  };
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activationMessage, setActivationMessage] = useState(null);

  // Create reducer with initial card information state
  // Returns the updated state to be passed to components and a function to update the state
  const [cardInfo, updateCardInfo] = useUpdateStateObject(initialCardInfoState);
  const [validationErrors, setValidationErrors] = useUpdateStateObject(
    initialValidationErrors
  );
  const [termsConsent, setTermsConsent] = useState(false);
  const [cardholderAgreement, setCardholderAgreement] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [electronicDisclosure, setElectronicDisclosure] = useState(false);

  const checkFormValidation = () => {
    let isInValid = false;
    ['cardNumber', 'expiry', 'cvv'].forEach(key => {
      const error = cardFieldError(key, cardInfo);
      setValidationErrors({ key, value: error });
      if (error) isInValid = true;
    });

    return isInValid;
  };

  const handleApiError = error => {
    let fieldName = '';

    switch (error.code) {
    case 'invalid_card_number':
      fieldName = 'cardNumber';
      break;
    case 'card_not_activated':
      fieldName = 'cardNumber';
      break;
    case 'failed_to_verify_card':
      fieldName = 'cvv';
      break;
    case 'failed_to_verify_card_expiry_date':
      fieldName = 'expiry';
      break;
    case 'failed_to_verify_card_expired':
      fieldName = 'expiry';
      break;
    case 'failed_to_verify_card_cvv':
      fieldName = 'cvv';
      break;
    default:
      break;
    }

    if (fieldName) {
      setValidationErrors({
        key: fieldName,
        // Fall back to the server message for codes missing from the catalog
        value: intl.messages[`errors-${error.code}`] ? `errors-${error.code}` : error.message,
      });
    } else {
      setFormError(intl.messages[`errors-${error.code}`] ? `errors-${error.code}` : error.message || 'An unknown error occurred.');
    }
  };
  // Set Handlers
  // eslint-disable-next-line consistent-return
  async function verifyCard() {
    // Run function to check for form errors.
    // Throws a generic error message if any of the fields are not valid
    if (checkFormValidation()) return;
    // The field groups the number in fours and takes the expiry as MM/YY; send the digits
    // and a four-digit year.
    const expiry = expandExpiry(cardInfo.expiry);

    const verifyPayload = {
      card_number: digitsOnly(cardInfo.cardNumber),
      cvv: cardInfo.cvv,
      expiry_month: expiry.slice(0, 2),
      expiry_year: expiry.slice(2),
    };

    try {
      setActivationMessage(null);
      setLoading(true);
      setFormError(false);

      let activationState = 'newlyactivated';

      const res = await post(
        '/api/v1/cardholder_portal/activate_card',
        verifyPayload
      );

      if (res.error) {
        const parsedError = {
          message: res.error.message || 'An unknown error occurred.',
          code: res.error.code || 'unknown_error',
        };
        throw parsedError; // throw a structured object
      }

      if (res.data.code === 'already_activated') {
        activationState = 'alreadyactivated';
      }

      // Clear All Input Fields and Error Values
      updateCardInfo({ cardNumber: '', cvv: '', expiry: '' });
      updateCardInfo({ key: 'cardNumber', value: '' });
      updateCardInfo({ key: 'cvv', value: '' });
      updateCardInfo({ key: 'expiry', value: '' });

      setValidationErrors({ key: 'cardNumber', value: '' });
      setValidationErrors({ key: 'cvv', value: '' });
      setValidationErrors({ key: 'expiry', value: '' });

      // Render Success Message
      setActivationMessage({ type: activationState });
    } catch (error) {
      handleApiError(error);
      setTermsConsent(false);
      setCardholderAgreement(false);
      setPrivacyPolicy(false);
      setElectronicDisclosure(false);
    }
    setLoading(false);
  }

  const handleCardFieldBlur = key => {
    setValidationErrors({ key, value: cardFieldError(key, cardInfo) });
  };

  const handleActivateKeyUp = async e => {
    if (e.keyCode === 13) {
      await verifyCard();
    }
  };

  const areAllAgreementsChecked = () => activationConsentsAccepted(theme.brand, {
    electronicCom: electronicDisclosure,
    terms: termsConsent,
    cardholderAgreement,
    privacyPolicy,
  });

  const submitButtonDisabled = !areAllAgreementsChecked();

  // TODO: Need to get correct wording for this page from Operations
  return (
    <AuthLayout
      page={page}
      heading={intl.messages[`${page}-heading`]}
      subHeaderTwo={intl.messages[`${page}-subheader2-text`]}
      error={formError}
    >
      <AuthForm
        page={page}
        onSubmit={e => {
          e.preventDefault();
          verifyCard();
        }}
        onKeyUp={handleActivateKeyUp}
        loading={loading}
        submitButtonDisabled={submitButtonDisabled}
      >
        {activationMessage && (
          <ActivationMessage type={activationMessage.type} />
        )}

        <CardFields
          values={cardInfo}
          errors={validationErrors}
          onChange={(key, value) => updateCardInfo({ key, value })}
          onBlur={handleCardFieldBlur}
          disabled={loading}
        />
        {(theme.brand.activateElectronicComConsentEn && theme.brand.activateElectronicComConsentFr)  && (
          <FormRow>
            <Checkbox
              label={(
                <FormattedMessage
                  id="activate-electronic-com-consent-label"
                  description="Label for Electronic Communication Consent"
                  defaultMessage={authEn['activate-electronic-com-consent-label']}
                  values={{
                    link: (
                      <ElectronicComConsentLink />
                    ),
                  }}
                />
              )}
              labelFor="activateElectronicCommConsentInput"
              id="activate-electronic-comm-consent-input"
              value={electronicDisclosure}
              onChange={e => setElectronicDisclosure(e.target.checked)}
              required
            />
          </FormRow>
        )}
        {(!theme.brand.activateCardholderAgreementEn || !theme.brand.activateCardholderAgreementFr) && (
          <FormRow>
            <Checkbox
              label={intl.messages['activate-terms-consent-label']}
              labelFor="termsConsentInput"
              id="terms-consent-input"
              value={termsConsent}
              onChange={e => setTermsConsent(e.target.checked)}
              required
            />
          </FormRow>
        )}
        {(theme.brand.activateCardholderAgreementEn && theme.brand.activateCardholderAgreementFr) && (
          <FormRow>
            <Checkbox
              label={(
                <FormattedMessage
                  id="activate-cardholder-agreement-label"
                  description="Label for Cardholder Agreement"
                  defaultMessage={authEn['activate-cardholder-agreement-label']}
                  values={{
                    link: (
                      <CardholderAgreementLink />
                    ),
                  }}
                />
              )}
              labelFor="activateCardholderAgreementInput"
              id="activate-cardholder-agreement-input"
              value={cardholderAgreement}
              onChange={e => setCardholderAgreement(e.target.checked)}
            />
          </FormRow>
        )}
        {(theme.brand.activatePrivacyPolicyEn && theme.brand.activatePrivacyPolicyFr) && (
          <FormRow>
            <Checkbox
              label={(
                <FormattedMessage
                  id="activate-privacy-policy-label"
                  description="Label for Privacy Policy"
                  defaultMessage={authEn['activate-privacy-policy-label']}
                  values={{
                    link: (
                      <PrivacyPolicyAgreementLink />
                    ),
                  }}
                />
              )}
              labelFor="activatePrivacyPolicyInput"
              id="activate-privacy-policy-input"
              value={privacyPolicy}
              onChange={e => setPrivacyPolicy(e.target.checked)}
              required
            />
          </FormRow>
        )}
      </AuthForm>
    </AuthLayout>
  );
};
